import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchPendingRequests } from "../api/requestsAdmin";
import { publishAssignedRequestCount } from "./useAssignedRequestCount";

/** Whether this load of the app has already decided where an approver lands, so going back to My Usage later is left alone. */
let checked = false;

/** Forgets that the landing page was decided, as a fresh load of the app does; for tests. */
export function resetLandingCheck(): void {
  checked = false;
}

/**
 * Decides where the app opens for a person who can decide requests: My Requests when requests are waiting for their decision,
 * otherwise the page asked for. It looks once per load of the app, so a person who goes on to open My Usage is not sent back.
 *
 * @param enabled - Whether the person holds `request.approve`; nobody else is checked.
 * @returns Whether the page can be shown: false while the check is under way, and when it is sending the person to My Requests.
 */
export function useLandingCheck(enabled: boolean): boolean {
  const navigate = useNavigate();
  const [settled, setSettled] = useState(checked);

  useEffect(() => {
    if (!enabled || checked) {
      return;
    }

    let cancelled = false;
    void fetchPendingRequests(false, true).then((result) => {
      if (cancelled) {
        return;
      }

      checked = true;
      if (result.kind === "ok" && Array.isArray(result.response) && result.response.length > 0) {
        publishAssignedRequestCount(result.response.length);
        void navigate("/my-requests", { replace: true });
        return;
      }

      setSettled(true);
    });

    return () => {
      cancelled = true;
    };
  }, [enabled, navigate]);

  return settled || !enabled;
}
